import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, ArrowUpRight, Shield } from 'lucide-react';
import logo from '@/assets/logo.png';

const InitiativeFooter = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const t = (ar: string, en: string) => (isRTL ? ar : en);

  return (
    <footer className="relative bg-[#040027] text-white/80 mt-auto">
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#FFA92C]/40 to-transparent" />

      <div className="container mx-auto px-4 py-14">
        <div className="grid md:grid-cols-3 gap-10">
          {/* Initiative identity */}
          <div className="md:col-span-1">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#FFA92C] font-bold">
              {t('من العالم — إلى غزة', 'From the world — to Gaza')}
            </div>
            <div className="mt-1 text-xl font-bold text-white leading-tight">
              {t('أبطال بلا حدود', 'Champions Without Borders')}
            </div>
            <p className="mt-4 text-sm leading-relaxed text-white/65">
              {t(
                '150 منحة تعليمية لمدربي كرة القدم في غزة، خلال 70 يوماً.',
                '150 educational scholarships for football coaches in Gaza, in 70 days.'
              )}
            </p>
          </div>

          {/* Hosts (separated identity) */}
          <div>
            <div className="text-xs uppercase tracking-wider text-white/50 font-bold mb-4">
              {t('الجهات المؤسسة', 'Hosting bodies')}
            </div>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <img src={logo} alt="Champions Academies" className="h-7 w-auto" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-bold text-white truncate">
                    {t('تشامبيونز أكاديمي', 'Champions Academies')}
                  </div>
                  <div className="text-xs text-white/55 mt-0.5">
                    {t('واجهة المبادرة', 'Initiative lead')}
                  </div>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-[#FFA92C]/20 to-[#FFA92C]/5 border border-[#FFA92C]/30 flex items-center justify-center flex-shrink-0">
                  <Shield className="h-5 w-5 text-[#FFA92C]" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-bold text-white truncate">
                    {t('نادي الجلاء الرياضي', 'Al-Jalaa Sports Club')}
                  </div>
                  <div className="text-xs text-white/55 mt-0.5">
                    {t('الشريك الميداني', 'On-the-ground partner')}
                  </div>
                </div>
              </li>
              <li className="pt-4 mt-2 border-t border-white/10">
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">
                  {t('بدعم تقني وتعليمي مستقل من', 'Independently supported by')}
                </div>
                <a
                  href="https://champions-hub.com"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 group"
                >
                  <img
                    src="/logos/champions-hub-horizontal-dark.png"
                    alt="Champions Hub"
                    className="h-7 w-auto opacity-90 group-hover:opacity-100 transition-opacity"
                  />
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <div className="text-xs uppercase tracking-wider text-white/50 font-bold mb-4">
              {t('تواصل', 'Contact')}
            </div>
            <ul className="space-y-3">
              <li>
                <a
                  href="mailto:partnerships@champions.ps"
                  className="group inline-flex items-center gap-2 text-sm text-white hover:text-[#FFA92C] transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  partnerships@champions.ps
                  <ArrowUpRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
                <div className="text-xs text-white/50 mt-0.5 ml-6">
                  {t('للرعاية والشراكات', 'Sponsorships & partnerships')}
                </div>
              </li>
              <li>
                <a
                  href="mailto:initiative@champions.ps"
                  className="group inline-flex items-center gap-2 text-sm text-white hover:text-[#FFA92C] transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  initiative@champions.ps
                  <ArrowUpRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
                <div className="text-xs text-white/50 mt-0.5 ml-6">
                  {t('لتقديم المدربين', 'Coach applications')}
                </div>
              </li>
            </ul>

            <div className="mt-6 flex flex-wrap gap-3 text-xs">
              <Link to="/initiative/coaches" className="text-white/70 hover:text-[#FFA92C]">
                {t('تقدّم الآن', 'Apply now')}
              </Link>
              <span className="text-white/20">·</span>
              <Link to="/initiative/sponsor" className="text-white/70 hover:text-[#FFA92C]">
                {t('كن راعياً', 'Become a sponsor')}
              </Link>
              <span className="text-white/20">·</span>
              <Link to="/" className="text-white/70 hover:text-[#FFA92C]">
                {t('الموقع الرئيسي', 'Main site')}
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-white/45">
          <span>
            © {new Date().getFullYear()} {t('تشامبيونز أكاديمي — جميع الحقوق محفوظة', 'Champions Academies — All rights reserved')}
          </span>
          <span>
            {t('بالتعاون مع نادي الجلاء، بدعم تقني من Champions Hub', 'In collaboration with Al-Jalaa Club, technically supported by Champions Hub')}
          </span>
        </div>
      </div>
    </footer>
  );
};

export default InitiativeFooter;
